/**
 * Rituals Tab
 * Displays community rituals in a 2-column grid with search
 * Conditionally renders AdminRitualsTab for admins
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useCommunityRituals } from "@/features/rituals/hooks/useCommunityRituals"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import type { CommunityDetail } from "../types/community-detail"
import { AdminRitualsTab } from "./AdminRitualTab"

interface RitualsTabProps {
  communityId: string
  searchQuery?: string
  isJoined?: boolean
  isPending?: boolean
  permissions?: CommunityDetail["permissions"]
}

export function RitualsTab({
  communityId,
  searchQuery = "",
  isJoined,
  isPending,
  permissions,
}: RitualsTabProps) {
  // Hooks MUST be called before any early returns (React Rules of Hooks)
  const { t } = useTranslation("communities")
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("all")

  // Toggle save mutation
  const { mutate: toggleSave } = useToggleRitualSave()

  // Use external searchQuery, debounced
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Fetch rituals with debounced search and status filter
  const { data, isLoading, isError } = useCommunityRituals(communityId, {
    limit: 50,
    search: debouncedSearch || undefined,
    status: activeTab as "all" | "archived" | "requests",
  })

  // If user has admin access, render AdminRitualsTab (after hooks)
  if (permissions?.canAddRituals) {
    return (
      <AdminRitualsTab
        communityId={communityId}
        searchQuery={searchQuery}
        isJoined={isJoined}
        isPending={isPending}
        permissions={permissions}
      />
    )
  }

  const totalCount = data?.total || 0

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      userAvatar: ritual.createdBy?.coverImage || undefined,
      username: ritual.createdBy?.name || t("rituals.unknown"),
      userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
      title: ritual.title,
      tags: [formatRitualType(ritual.type)],
      duration: `${ritual.duration} min`,
      contextualTag: formatRitualType(ritual.type),
      userId: ritual.createdBy.id,
      price: ritual.price,
      participationFee: ritual.participationFee,
      organizersCount: ritual.organizerCount,
      isBookmarked: ritual.isSaved ?? false,
      onBookmarkClick: () => toggleSave(ritual.id),
    })) || []

  return (
    <div>
      {/* Header with tabs */}
      <div className="flex items-center justify-start mb-4 mt-9 gap-4 h-10">
        <h2 className="text-lg font-semibold text-charcoal font-montserrat uppercase">
          {t("rituals.title")}
        </h2>
        <div className="flex items-center border-l border-body">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`px-4 py-1 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === "all"
                ? "border-primary text-charcoal"
                : "border-transparent text-stone hover:text-charcoal"
            }`}
          >
            {t("rituals.all")} ({totalCount})
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("rituals.loading")}</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("rituals.error")}</p>
        </div>
      )}

      {/* Rituals Grid */}
      {!isLoading && !isError && (
        <div className="">
          {rituals.length === 0 ? (
            <div className="h-[360px] py-7">
              <p className="text-lg font-semibold text-charcoal mb-0 text-[18px] font-monteserat uppercase">
                {t("rituals.emptyTitle")}
              </p>
              <p className="text-sm mt-4 text-stone">{t("rituals.emptyDescription")}</p>
              {!isJoined && !isPending && (
                <Button
                  variant="primary"
                  className="mt-6 px-20 uppercase"
                  onClick={() => {
                    // Trigger join via parent - this is handled by parent component
                    // We'll use a custom event or prop callback pattern
                    window.dispatchEvent(new CustomEvent("community-join-click"))
                  }}
                >
                  {t("rituals.emptyJoinCommunity")}
                </Button>
              )}
              {isJoined && !isPending && permissions?.canAddRituals && (
                <Button
                  variant="primary"
                  className="mt-6 px-20 uppercase"
                  onClick={() => {
                    navigate(`/rituals/create?communityId=${communityId}`, {
                      state: { canAddCommunityAsOrganizer: permissions?.canAddRituals },
                    })
                  }}
                >
                  {t("rituals.emptyAddRitual")}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-9">
              {rituals.map(ritual => (
                <div key={ritual.id} className="h-[440px]">
                  <RitualCard {...ritual} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
